import React, { useEffect, useState } from 'react';
import { Alert, Button, Select, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { fetchSupplierOrders, fetchSupplierOrder, getSupplierOrderDisplayNumber } from '../../orders/api/supplierOrdersApi';
import { fetchOrders, fetchOrderDetail } from '../../orders/api/ordersApi';
import { buildPurchaseSourceLineCandidates } from '../purchaseInvoiceCandidates';
import { sourceQuantitySetFromLine } from '../workflowInvoiceQuantity';
import type { WorkflowInvoiceDirection, WorkflowInvoiceSourceLineCandidate } from '../../../types/workflowInvoice';

type OrderOption = { value: string; label: string; supplierId?: string };
interface Props {
  direction: WorkflowInvoiceDirection;
  ids: string[];
  disabled?: boolean;
  onChange: (ids: string[], supplierId?: string) => void;
  onLoading?: (loading: boolean) => void;
  onCandidates: (candidates: WorkflowInvoiceSourceLineCandidate[]) => void;
}

export default function InvoiceOrderPicker({ direction, ids, disabled, onChange, onCandidates, onLoading }: Props) {
  const { t } = useTranslation('invoices');
  const [options, setOptions] = useState<OrderOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [listFailed, setListFailed] = useState(false);
  const [linesFailed, setLinesFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setListFailed(false);
    const request = direction === 'PURCHASE'
      ? fetchSupplierOrders().then(rows => rows.map(row => ({ value: row.id, label: [getSupplierOrderDisplayNumber(row), row.supplier?.name].filter(Boolean).join(' — '), supplierId: row.supplierId })))
      : fetchOrders().then(rows => rows.map(row => ({ value: row.id, label: [row.displayOrderNumber, row.customerName].filter(Boolean).join(' — ') })));
    request.then(rows => { if (active) setOptions(rows); }).catch(() => { if (active) setListFailed(true); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [direction, retry]);
  const idsKey = ids.join(',');
  useEffect(() => {
    let active = true;
    onCandidates([]);
    onLoading?.(ids.length > 0);
    setLinesFailed(false);
    const request = direction === 'PURCHASE'
      ? Promise.all(ids.map(id => fetchSupplierOrder(id))).then(rows => rows.flatMap(buildPurchaseSourceLineCandidates))
      : Promise.all(ids.map(id => fetchOrderDetail(id))).then(rows => rows.flatMap(row => row.products.map(item => ({
        sourceOrderLineId: item.id, orderNumber: row.displayOrderNumber, productName: item.name,
        productVariantId: item.productVariantId, variantLabel: item.variantName,
        width: item.width ?? item.catalogProductWidth, height: item.height ?? item.catalogProductHeight,
        length: item.length ?? item.catalogProductLength, thickness: item.thickness ?? item.catalogProductThickness,
        sourceQuantitySet: sourceQuantitySetFromLine(item),
      }))));
    request.then(rows => { if (active) { onCandidates(rows); setLinesFailed(false); } }).catch(() => { if (active) { onCandidates([]); setLinesFailed(true); } }).finally(() => { if (active) onLoading?.(false); });
    return () => { active = false; };
    // Candidate refresh belongs to the selected source identities, not form edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, idsKey, retry]);
  const supplier = options.find(option => ids.includes(option.value))?.supplierId;
  return <Space direction="vertical" style={{ width: '100%' }}>
    <Typography.Text>{t('workflow.page.sourceOrders')}</Typography.Text>
    <Typography.Text type="secondary">{t(direction === 'PURCHASE' ? 'workflow.page.sourceOrdersHint' : 'workflow.page.salesSourceOrdersHint')}</Typography.Text>
    <Select
      mode={direction === 'PURCHASE' ? 'multiple' : undefined}
      labelRender={({ value, label }) => options.find(option => option.value === value)?.label || t('workflow.page.sourceOrder')}
      showSearch optionFilterProp="label" allowClear loading={loading} disabled={disabled}
      aria-label={t('workflow.page.sourceOrders')}
      placeholder={t('workflow.page.sourceOrders')}
      style={{ width: '100%' }}
      value={direction === 'PURCHASE' ? ids : ids[0]}
      options={options.map(option => ({ ...option, disabled: Boolean(supplier && option.supplierId && supplier !== option.supplierId) }))}
      onChange={(value: string[] | string | undefined) => {
        const next = Array.isArray(value) ? value : value ? [value] : [];
        onChange(next, options.find(option => next.includes(option.value))?.supplierId);
      }}
    />
    {ids.length > 0 && !listFailed && !linesFailed && <Button type="link" onClick={() => setRetry(value => value + 1)}>{t('workflow.page.retrySources')}</Button>}
    {(listFailed || linesFailed) && <Alert type="warning" showIcon message={t(listFailed ? 'workflow.page.ordersLoadFailed' : 'workflow.page.linesLoadFailed')} action={<Button onClick={() => setRetry(value => value + 1)}>{t('workflow.page.retrySources')}</Button>} />}
  </Space>;
}
